import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, TrendingUp, Search, Moon, Sun, Bell, User as UserIcon, LogOut, MessageCircle, UserPlus, Check, Trash2, Banknote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useGlobalChat } from '../../contexts/GlobalChatContext';
import { getRoleLabel } from '../../lib/roles';

interface TopbarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  menuItems: Array<{ id: string; label: string; icon: React.ElementType; color: string }>;
}

export const Topbar: React.FC<TopbarProps> = ({ sidebarOpen, setSidebarOpen, menuItems }) => {
  const navigate = useNavigate();
  const { user, company, logout, accessibleCompanies, switchCompany } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const { openChat } = useGlobalChat();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; label: string; icon: React.ElementType; color: string }>>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const notificationsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchQuery.trim() !== '') {
      const filteredItems = menuItems.filter(item =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filteredItems);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, menuItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
      if (notificationsDropdownRef.current && !notificationsDropdownRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (tabId: string) => {
    navigate(`/${tabId === 'dashboard' ? '' : tabId}`);
    setSearchQuery('');
    setIsSearchFocused(false);
  };


  return (
    <div className="print:hidden fixed top-0 left-0 right-0 z-50 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md border-b border-white/20 dark:border-gray-700/50 shadow-sm transition-colors duration-300">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          <button aria-label="Abrir ou fechar menu"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 text-gray-700 dark:text-gray-200"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div className="flex items-center space-x-3">
            {company?.settings?.theme?.logoUrl ? (
              <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                <img 
                  src={company.settings.theme.logoUrl} 
                  alt={company.name} 
                  className="w-full h-full object-cover" 
                />
              </div>
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                <TrendingUp size={18} className="text-white" />
              </div>
            )}
            <div className="hidden lg:block">
              {(accessibleCompanies?.length ?? 0) > 1 ? (
                <select
                  aria-label="Trocar de loja"
                  value={company?.id ?? ''}
                  onChange={(e) => switchCompany(Number(e.target.value))}
                  className="font-bold text-gray-800 dark:text-white bg-transparent border-none p-0 pr-6 focus:ring-0 cursor-pointer"
                >
                  {accessibleCompanies.map(c => (
                    <option key={c.id} value={c.id} className="text-gray-800">{c.name}</option>
                  ))}
                </select>
              ) : (
                <h1 className="font-bold text-gray-800 dark:text-white transition-colors">{company?.name}</h1>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">Orion CRM</p>
            </div>
          </div>
        </div>

        {/* Center - Search */}
        {/* Mobile Search Toggle */}
        <div className="md:hidden flex items-center pr-2">
          <button aria-label="Buscar"
            onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Search size={20} />
          </button>
        </div>

        <div
          className={`${isMobileSearchOpen ? 'flex absolute top-16 left-0 right-0 px-4 pb-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-md' : 'hidden'} md:static md:flex md:border-none md:shadow-none md:p-0 md:bg-transparent flex-1 max-w-md mx-8 relative transition-all duration-300 z-50`}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
        >
          <div className="relative w-full">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <input
              id="topbar-search"
              name="topbar-search"
              type="text"
              placeholder="Buscar funcionalidade..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 transition-all"
            />
          </div>

          {isSearchFocused && searchQuery && (
            <div className="absolute top-full mt-2 w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-2 z-50">
              {searchResults.length > 0 ? (
                searchResults.map(item => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        handleResultClick(item.id);
                        setIsMobileSearchOpen(false);
                      }}
                      className="w-full flex items-center p-3 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Icon size={18} className={`mr-3 ${item.color}`} />
                      <span>{item.label}</span>
                    </button>
                  );
                })
              ) : (
                <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                  Nenhum resultado encontrado.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-3">
          <button
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 text-gray-600 dark:text-gray-300"
            title={theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>

          <div className="relative" ref={notificationsDropdownRef}>
            <button
              aria-label={unreadCount > 0 ? `Notificações (${unreadCount} novas)` : 'Notificações'}
              aria-expanded={isNotificationsOpen}
              className="p-2 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 relative"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            >
              <Bell size={18} className="text-gray-600 dark:text-gray-300" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {isNotificationsOpen && (
              <div className="fixed left-4 right-4 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:w-96 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden z-50 transform origin-top-right transition-all">
                <div className="p-4 bg-gray-50/50 dark:bg-gray-900/50 border-b border-gray-200/50 dark:border-gray-700/50 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-800 dark:text-white">Notificações</h3>
                    {unreadCount > 0 && (
                      <span className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 text-[11px] font-bold px-2 py-0.5 rounded-full">
                        {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors"
                        title="Marcar todas como lidas"
                      >
                        <Check size={14} />
                        Ler
                      </button>
                    )}
                    {notifications.length > 0 && (
                      <button
                        onClick={clearAll}
                        className="text-xs flex items-center gap-1 text-gray-500 hover:text-red-600 transition-colors"
                        title="Limpar todas as notificações"
                      >
                        <Trash2 size={14} />
                        Limpar
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                  {notifications.length === 0 ? (
                    <div className="p-8 flex flex-col items-center justify-center text-center text-gray-500 dark:text-gray-400">
                      <Bell size={24} className="mb-2 opacity-20" />
                      <p className="text-sm">Nenhuma notificação</p>
                      <p className="text-xs mt-1">Você está em dia!</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {notifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer relative flex gap-3 ${!notification.read ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}
                          onClick={() => {
                            markAsRead(notification.id);
                            if (notification.type === 'message' && notification.data?.leadObj) {
                                openChat(notification.data.leadObj as any);
                                setIsNotificationsOpen(false);
                            } else if (notification.link) {
                              navigate(notification.link.startsWith('/') ? notification.link : `/${notification.link}`);
                              setIsNotificationsOpen(false);
                            }
                          }}
                        >
                          {!notification.read && (
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                          )}
                          
                          {/* Icon Circle */}
                          <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${notification.type === 'message'
                              ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                              : notification.type === 'payroll'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}>
                            {notification.type === 'message' ? <MessageCircle size={14} /> : notification.type === 'payroll' ? <Banknote size={14} /> : <UserPlus size={14} />}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-1">
                              <p className={`text-sm pr-2 truncate ${!notification.read ? 'font-bold text-gray-900 dark:text-white' : 'font-medium text-gray-800 dark:text-gray-200'}`}>
                                {notification.title}
                              </p>
                              <span className="text-[11px] text-gray-500 whitespace-nowrap flex-shrink-0">
                                {new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className={`text-xs line-clamp-2 ${!notification.read ? 'text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                              {notification.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={profileDropdownRef}>
            <button
              aria-label="Menu do usuário"
              aria-expanded={isProfileDropdownOpen}
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-xl px-3 py-2 shadow-md hover:shadow-lg transition-all"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                {user?.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-800 dark:text-white">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getRoleLabel(user?.role)}
                </p>
              </div>
            </button>

            {isProfileDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-2 z-50">
                <div className="p-3 border-b border-gray-200/50 dark:border-gray-700/50 min-w-0">
                  <p className="font-semibold text-gray-800 dark:text-white truncate">{user?.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 break-all">{user?.email}</p>
                </div>
                {/* No celular o seletor de loja do topo fica escondido; aqui ele aparece */}
                {(accessibleCompanies?.length ?? 0) > 1 && (
                  <div className="p-3 border-b border-gray-200/50 dark:border-gray-700/50 lg:hidden">
                    <label htmlFor="mobile-company-switch" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Loja</label>
                    <select
                      id="mobile-company-switch"
                      value={company?.id ?? ''}
                      onChange={(e) => switchCompany(Number(e.target.value))}
                      className="w-full text-base rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white px-2 py-2"
                    >
                      {accessibleCompanies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="p-1">
                  <button
                    onClick={() => { navigate('/profile'); setIsProfileDropdownOpen(false); }}
                    className="w-full flex items-center p-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <UserIcon size={16} className="mr-2" />
                    Meu Perfil
                  </button>
                  <button
                    onClick={logout}
                    className="w-full flex items-center p-2 text-left text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <LogOut size={16} className="mr-2" />
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
